import { z } from "zod";
import type { WorkDetails } from "../WorkDetailsForm";
import type { CoordinatesValue } from "@/types/supabase-helpers";

// ============ FORM SCHEMA ============
export const orderSchema = z.object({
  customer_id: z.string().min(1, "Asiakas on pakollinen"),
  partner_id: z.string().optional(),
  cemetery_id: z.string().optional(),
  order_date: z.date(),
  installation_date: z.date().optional(),
  delivery_date: z.date().optional(),
  order_type: z.enum(["new_work", "pickup_work", "field_work"]),
  fulfillment_type: z.enum(["delivery", "installation", "pickup", "return"]),
  status: z.enum(["draft", "confirmed", "ready_for_installation", "installed", "completed", "cancelled"]),
  notes: z.string().optional(),
  commission_type: z.enum(["fixed", "percentage"]).optional(),
  commission_value: z.number().optional(),
  requires_cemetery_approval: z.boolean().optional(),
});

export type OrderFormData = z.infer<typeof orderSchema>;

// ============ ORDER LINE ============
export interface OrderLine {
  id: string;
  product_id: string | null;
  configuration_id?: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  vat_rate: number;
  line_total: number;
  price_includes_vat: boolean;
  work_details?: WorkDetails | null;
}

// ============ CONFIGURATION ============
export interface ConfigAttribute {
  id: string;
  attributeId: string;
  name: string;
  label: string;
  type: string;
  isRequired: boolean;
  options: string[] | null;
  value: string | number | CoordinatesValue | null;
}

export interface ConfigComponent {
  id: string;
  modelComponentId: string;
  componentTypeId: string;
  componentTypeName: string;
  componentTypeLabel: string;
  materialId: string | null;
  quantity: number;
  minQuantity: number;
  maxQuantity: number;
  width: number | null;
  height: number | null;
  thickness: number | null;
  allowedMaterials: { id: string; name: string; isDefault: boolean }[];
  attributes: ConfigAttribute[];
  sourceModelName?: string;
}

export interface Configuration {
  productModelId: string;
  productModelName: string;
  customName?: string;  // Käyttäjän antama vapaavalintainen nimi
  components: ConfigComponent[];
}

// ============ INLINE FORM DATA ============
export interface CustomerFormData {
  type: "private" | "company" | "municipality" | "estate";
  first_name: string;
  last_name: string;
  company_name: string;
  business_id: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  postal_code: string;
  city: string;
}

export interface PartnerFormData {
  type: "installer" | "workshop";
  name: string;
  business_id: string;
  email: string;
  phone: string;
  address: string;
  postal_code: string;
  city: string;
}

export interface CemeteryFormData {
  name: string;
  address: string;
  postal_code: string;
  city: string;
}

// ============ QUERY DATA ============
export interface CustomerOption {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  phone: string | null;
  email: string | null;
}

export interface PartnerOption {
  id: string;
  name: string;
}

export interface CemeteryOption {
  id: string;
  name: string;
  city: string;
  requires_approval: boolean;
}

export interface ProductOption {
  id: string;
  name: string;
  description: string | null;
  unit: string | null;
  price: number;
  price_includes_vat: boolean;
  vat_rate: number;
  image_url: string | null;
  category: string | null;
}

// ============ MISC ============
export interface OrderTrackingInfo {
  order_number: string;
  tracking_token: string;
}

// ============ INITIAL VALUES ============
export const INITIAL_CUSTOMER_FORM: CustomerFormData = {
  type: "private",
  first_name: "",
  last_name: "",
  company_name: "",
  business_id: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
};

export const INITIAL_PARTNER_FORM: PartnerFormData = {
  type: "installer",
  name: "",
  business_id: "",
  email: "",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
};

export const INITIAL_CEMETERY_FORM: CemeteryFormData = {
  name: "",
  address: "",
  postal_code: "",
  city: "",
};

export const DEFAULT_VAT_RATE = 25.5;

// ============ HELPERS ============
export function getCustomerDisplayName(customer: CustomerOption | null): string {
  if (!customer) return "";
  if (customer.company_name) return customer.company_name;
  return `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
}
